import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import {
  Users, Eye, FileText, User, Loader2, AlertCircle, X,
  Calendar, BookOpen, CheckCircle, Edit, Trash2, Download,
  Mail, GraduationCap, Filter, ChevronLeft, ChevronRight, MoreVertical,
  Printer, FileJson, FileSpreadsheet, Save, TrendingUp, History, Database, ClipboardCheck
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import { TERM_OPTIONS, ASSESSMENT_TYPES } from "../../constants/assessments";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Class { id: string; name: string; studentCount?: number; }
interface Student { id: string; name: string; email: string; className?: string; classId?: string; }
interface TestResult {
  id: string;
  score: number;
  test: {
    id: string;
    title: string;
    term?: string;
    date: string;
    subject: { name: string; };
  };
  createdAt: string;
}
interface Subject { id: string; name: string; short_name?: string | null; }
type ModalType = "viewResults" | "recordResults" | "viewDetails" | null;

const abbreviateSubject = (name: string) => {
  const cleaned = name.trim().toUpperCase();
  if (cleaned === "CREATIVE AND TECHNOLOGY STUDIES") return "CTS";
  if (cleaned === "HOME ECONOMICS") return "HE";
  if (cleaned === "PHYSICAL EDUCATION") return "P.E.";
  if (cleaned === "SCIENCE PAPER 1") return "SP1";
  if (cleaned === "SCIENCE PAPER 2") return "SP2";
  return name;
};

const getSubjectLabel = (subject: Subject) => {
  const shortName = typeof subject.short_name === "string" ? subject.short_name.trim() : "";
  return shortName || abbreviateSubject(subject.name);
};

const AssessmentsPage: React.FC = () => {
  const getDefaultTerm = () => {
    const month = new Date().getMonth() + 1;
    if (month >= 9) return "Term Three";
    if (month >= 5) return "Term Two";
    return "Term One";
  };

  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [studentResults, setStudentResults] = useState<TestResult[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [classSubjects, setClassSubjects] = useState<Subject[]>([]);
  const [recordFormData, setRecordFormData] = useState({
    examType: "",
    term: getDefaultTerm(),
    testDate: new Date().toISOString().split('T')[0],
    scores: {} as Record<string, string>,
  });
  const [recordMessage, setRecordMessage] = useState<string | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  const [studentsCurrentPage, setStudentsCurrentPage] = useState(1);
  const studentsPerPage = 8;
  const [editingResult, setEditingResult] = useState<TestResult | null>(null);
  const [editScore, setEditScore] = useState("");
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [filterExamType, setFilterExamType] = useState("");
  const [filterTerm, setFilterTerm] = useState("");
  const [filterYear, setFilterYear] = useState("");

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();

  const fetchAllStudents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const isTeacher = role === "teacher";
      let classesData: Class[] = [];

      if (isTeacher) {
        const dashboardRes = await axios.get(getApiUrl("teacher/dashboard"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        classesData = dashboardRes.data.classes || [];
      } else {
        const classesRes = await axios.get(getApiUrl("academics/classes"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        classesData = Array.isArray(classesRes.data) ? classesRes.data : classesRes.data.classes || [];
      }

      const classes = classesData || [];
      if (classes.length === 0) {
        setStudents([]);
        setLoading(false);
        return;
      }
      // Fetch students for all classes in parallel
      const fetchPromises = classes.map(async (cls) => {
        try {
          const classId = (cls as any).id || (cls as any)._id;
          const endpoint = isTeacher ? `teacher/classes/${classId}/students` : `academics/classes/${classId}/students`;
          const res = await axios.get(getApiUrl(endpoint), {
            headers: { Authorization: `Bearer ${token}` },
          });
          return (res.data || []).map((s: any) => ({ ...s, className: cls.name, classId }));
        } catch {
          return [];
        }
      });
      const results = await Promise.all(fetchPromises);
      // Remove duplicate students
      const unique = results.flat().reduce((acc, s) => {
        if (!acc.find((x: any) => x.id === s.id)) acc.push(s);
        return acc;
      }, [] as Student[]);
      setStudents(unique);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load student records");
    } finally {
      setLoading(false);
    }
  }, [token, role]);

  useEffect(() => { if (token) fetchAllStudents(); }, [fetchAllStudents, token]);

  const fetchClassSubjects = useCallback(async (classId: string) => {
    setLoadingSubjects(true);
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${classId}/subjects`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const subjects = res.data || [];
      setClassSubjects(subjects);
      const scores: Record<string, string> = {};
      subjects.forEach((s: any) => scores[s.id] = "");
      setRecordFormData(prev => ({ ...prev, scores }));
    } catch { setClassSubjects([]); }
    finally { setLoadingSubjects(false); }
  }, [token]);

  const fetchStudentResults = useCallback(async (studentId: string) => {
    setResultsLoading(true);
    try {
      const res = await axios.get(getApiUrl(`assessments/results/student/${studentId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudentResults(res.data || []);
    } catch { setStudentResults([]); }
    finally { setResultsLoading(false); }
  }, [token]);

  const openRecordModal = (student: Student) => {
    setSelectedStudent(student);
    setActiveModal("recordResults");
    setRecordMessage(null); setRecordError(null);
    setRecordFormData({ examType: "", term: getDefaultTerm(), testDate: new Date().toISOString().split('T')[0], scores: {} });
    if (student.classId) fetchClassSubjects(student.classId);
  };

  const openViewModal = (student: Student) => {
    setSelectedStudent(student);
    setActiveModal("viewResults");
    fetchStudentResults(student.id);
    setFilterExamType(""); setFilterTerm(""); setFilterYear("");
  };

  const closeModal = () => {
    setActiveModal(null); setSelectedStudent(null);
    setEditingResult(null); setRecordMessage(null); setRecordError(null);
  };

  const handleScoreChange = (subjectId: string, value: string) => {
    setRecordFormData(prev => ({
      ...prev,
      scores: {
        ...prev.scores,
        [subjectId]: value
      }
    }));
  };

  const handleRecordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent?.classId || !recordFormData.examType || !recordFormData.term) {
      setRecordError("Class, Term, and Assessment Type are required.");
      return;
    }
    const scores = Object.entries(recordFormData.scores)
      .filter(([_, val]) => val.trim() !== "")
      .map(([subId, val]) => ({
        studentId: selectedStudent.id,
        subjectId: subId,
        score: parseFloat(val)
      }));

    if (scores.length === 0) { setRecordError("Please enter at least one score."); return; }

    setRecording(true); setRecordError(null);
    try {
      const year = new Date(recordFormData.testDate).getFullYear();
      await axios.post(getApiUrl("assessments/results/bulk"), {
        classId: selectedStudent.classId,
        examType: recordFormData.examType,
        term: recordFormData.term,
        year,
        scores
      }, { headers: { Authorization: `Bearer ${token}` } });
      setRecordMessage("Assessment results saved successfully.");
      setTimeout(closeModal, 1500);
    } catch (err: any) {
      setRecordError(err.response?.data?.message || "Failed to save assessment results. Please try again.");
    } finally { setRecording(false); }
  };

  const handleUpdateScore = async (id: string, newScore: number) => {
    setUpdating(true);
    try {
      await axios.put(getApiUrl(`assessments/results/${id}`), { score: newScore }, { headers: { Authorization: `Bearer ${token}` } });
      if (selectedStudent) fetchStudentResults(selectedStudent.id);
      setEditingResult(null);
    } catch { setRecordError("Update failed."); }
    finally { setUpdating(false); }
  };

  const handleDeleteResult = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this assessment record?")) return;
    setDeleting(id);
    try {
      await axios.delete(getApiUrl(`assessments/results/${id}`), { headers: { Authorization: `Bearer ${token}` } });
      if (selectedStudent) fetchStudentResults(selectedStudent.id);
    } catch { alert("Failed to delete record. Please try again."); }
    finally { setDeleting(null); }
  };

  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.className || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const studentsTotalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const currentStudents = filteredStudents.slice((studentsCurrentPage - 1) * studentsPerPage, studentsCurrentPage * studentsPerPage);

  const getFilteredResults = () => {
    let filtered = [...studentResults];
    if (filterExamType) {
      filtered = filtered.filter(r => (r.test?.title || (r as any).testTitle || (r as any).examType) === filterExamType);
    }
    if (filterTerm) {
      filtered = filtered.filter(r => ((r as any).term || r.test?.term) === filterTerm);
    }
    if (filterYear) {
      filtered = filtered.filter(r => {
        const dateStr = r.test?.date || (r as any).testDate;
        if (dateStr) return new Date(dateStr).getFullYear().toString() === filterYear;
        const yearVal = (r as any).year;
        return yearVal ? String(yearVal) === filterYear : false;
      });
    }
    return filtered;
  };

  const exportExcel = () => {
    const data = getFilteredResults();
    if (!selectedStudent || data.length === 0) return;

    const wb = XLSX.utils.book_new();
    const className = selectedStudent.className || "N/A";
    const reportDate = new Date().toLocaleDateString();

    const rows = [
      ["MILESTONE SCHOOL - STUDENT PERFORMANCE ANALYTICS"],
      [`Student: ${selectedStudent.name} | Class: ${className}`],
      [`Generated on: ${reportDate}`],
      [""],
      ["#", "Subject/Paradigm", "Cycle/Assessment", "Evaluation Date", "Index %", "Grade", "Status"]
    ];

    data.forEach((r, i) => {
      const score = r.score;
      const grade = score >= 80 ? "A" : score >= 70 ? "B" : score >= 60 ? "C" : score >= 50 ? "D" : "F";
      const status = score >= 50 ? "PASSED" : "FAILED";
      rows.push([
        (i + 1).toString(),
        r.test.subject.name.toUpperCase(),
        r.test.title.toUpperCase(),
        new Date(r.test.date).toLocaleDateString(),
        score.toString(),
        grade,
        status
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Styling configuration
    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const lastCol = range.e.c;
      const lastRow = range.e.r;
      // Set Column Widths
      ws["!cols"] = [
        { wch: 5 },  // #
        { wch: 30 }, // 
        { wch: 25 }, // 
        { wch: 15 }, // 
        { wch: 10 }, // %
        { wch: 8 },
        { wch: 12 }
      ];
      // Merge Main Headers
      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } });
      ws["!merges"].push({ s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } });
      ws["!merges"].push({ s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } });
      // Apply Styles to All Cells
      for (let r = 0; r <= lastRow; r++) {
        for (let c = 0; c <= lastCol; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) continue;
          // Default style
          ws[addr].s = {
            font: { name: "Arial", sz: 10 },
            alignment: { vertical: "center", horizontal: "left", wrapText: true },
            border: {
              top: { style: "thin", color: { rgb: "E2E8F0" } },
              bottom: { style: "thin", color: { rgb: "E2E8F0" } },
              left: { style: "thin", color: { rgb: "E2E8F0" } },
              right: { style: "thin", color: { rgb: "E2E8F0" } }
            }
          };
          // Main Header Row 0
          if (r === 0) {
            ws[addr].s = {
              font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "1E40AF" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
          // Info Rows 1 & 2
          else if (r === 1 || r === 2) {
            ws[addr].s = {
              font: { bold: true, sz: 11, color: { rgb: "1E293B" } },
              fill: { fgColor: { rgb: "F1F5F9" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
          // Table Headers Row 4
          else if (r === 4) {
            ws[addr].s = {
              font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10 },
              fill: { fgColor: { rgb: "334155" } },
              alignment: { horizontal: "center", vertical: "center" },
              border: {
                top: { style: "thin", color: { rgb: "000000" } },
                bottom: { style: "thin", color: { rgb: "000000" } }
              }
            };
          }
          // Data Rows
          else if (r > 4) {
            // Centered columns
            if (c === 0 || c >= 3) {
              ws[addr].s.alignment.horizontal = "center";
            }
            // Score based coloring for last few columns
            if (c === 4) {
              const val = parseFloat(ws[addr].v);
              if (val < 50) ws[addr].s.font.color = { rgb: "EF4444" };
              else if (val >= 80) ws[addr].s.font.color = { rgb: "10B981" };
              ws[addr].s.font.bold = true;
            }
            // Zebra striping
            if ((r - 5) % 2 === 0) {
              ws[addr].s.fill = { fgColor: { rgb: "F8FAFC" } };
            }
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, "Performance Registry");
    XLSX.writeFile(wb, `${selectedStudent.name}_Performance.xlsx`);
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Assessment Management</h1>
            <p className="page-subtitle">View and record student assessment results across all classes</p>
          </div>
        </div>

        <div className="utopia-page-container">
          <table className="utopia-table">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Class</th>
                <th>Email</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentStudents.map(s => (
                <tr key={s.id} className="group/row">
                  <td>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 font-black text-xs shadow-inner group-hover/row:bg-teal-50 group-hover/row:text-teal-600 transition-all">
                        {s.name.charAt(0)}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-black text-slate-800 uppercase tracking-tight text-sm">{s.name}</span>
                        <span className="text-[9px] text-slate-400 font-mono tracking-widest uppercase">ID: {String(s.id || '').slice(-8)}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="utopia-badge bg-white text-slate-500 border border-slate-100 font-black text-[9px] uppercase tracking-widest px-3">
                      {s.className || "Not Assigned"}
                    </span>
                  </td>
                  <td className="text-slate-400 font-medium text-xs font-mono">{s.email}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button className="utopia-btn-secondary h-10 px-4 text-[10px] font-black uppercase tracking-widest flex items-center gap-2" onClick={() => openViewModal(s)}>
                        <TrendingUp size={12} /> Analytics
                      </button>
                      <button className="utopia-btn-primary h-10 px-4 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-teal-50" onClick={() => openRecordModal(s)}>
                        <ClipboardCheck size={12} /> Record
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {studentsTotalPages > 1 && (
            <div className="utopia-pagination p-8 border-t border-slate-50">
              <button className="utopia-page-btn" disabled={studentsCurrentPage === 1} onClick={() => setStudentsCurrentPage(p => p - 1)}><ChevronLeft size={16} /></button>
              <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest mx-6">Sequence {studentsCurrentPage} / {studentsTotalPages}</span>
              <button className="utopia-page-btn" disabled={studentsCurrentPage === studentsTotalPages} onClick={() => setStudentsCurrentPage(p => p + 1)}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>
      </main>

      {/* VIEW RESULTS MODAL */}
      {activeModal === "viewResults" && selectedStudent && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal-content max-w-6xl h-[90vh] flex flex-col p-0 overflow-hidden">
            <div className="utopia-modal-header bg-slate-50 p-8 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-white rounded-3xl shadow-sm flex items-center justify-center text-teal-600 border border-slate-100"><TrendingUp size={32} /></div>
                <div>
                  <h3 className="text-2xl font-black text-slate-800 tracking-tighter">Student Performance</h3>
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">{selectedStudent.name} • {selectedStudent.className || "Class N/A"}</p>
                </div>
              </div>
              <button className="p-4 hover:bg-white rounded-full transition-all text-slate-400 hover:text-slate-900 shadow-sm" onClick={closeModal}><X size={24} /></button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 flex-1 overflow-hidden">
              {/* Filters Sidebar */}
              <div className="lg:col-span-1 border-r border-slate-50 bg-slate-50/20 p-8 flex flex-col gap-8">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Assessment Type</label>
                    <select className="utopia-input h-14 text-sm font-black bg-white" value={filterExamType} onChange={e => setFilterExamType(e.target.value)}>
                      <option value="">All Assessment Types</option>
                      {ASSESSMENT_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Term</label>
                    <select className="utopia-input h-14 text-sm font-black bg-white" value={filterTerm} onChange={e => setFilterTerm(e.target.value)}>
                      <option value="">All Terms</option>
                      {TERM_OPTIONS.map(term => (
                        <option key={term} value={term}>{term}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Year</label>
                    <select className="utopia-input h-14 text-sm font-black bg-white" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                      <option value="">All Years</option>
                      <option value="2024">2024</option>
                      <option value="2025">2025</option>
                      <option value="2026">2026</option>
                    </select>
                  </div>
                </div>

                <div className="mt-auto space-y-3">
                  <button className="utopia-btn-secondary w-full py-4 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2" onClick={exportExcel}>
                    <FileSpreadsheet size={16} /> Export CSV
                  </button>
                  <button className="utopia-btn-secondary w-full py-4 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 opacity-50 cursor-not-allowed">
                    <Printer size={16} /> PDF Registry
                  </button>
                </div>
              </div>

              {/* Results Registry */}
              <div className="lg:col-span-3 flex flex-col overflow-hidden bg-white">
                <div className="flex-1 overflow-y-auto p-10">
                  {resultsLoading ? (
                    <div className="py-40 text-center"><Loader2 className="mx-auto animate-spin text-teal-200 opacity-20" size={64} /></div>
                  ) : getFilteredResults().length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-8">
                        <h4 className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">Assessment Results</h4>
                        <div className="h-px flex-1 mx-6 bg-slate-50"></div>
                        <span className="text-[10px] font-black text-teal-500 uppercase">{getFilteredResults().length} Records</span>
                      </div>
                      <table className="utopia-table">
                        <thead>
                          <tr>
                            <th>Subject</th>
                            <th>Assessment Type</th>
                            <th className="text-center">Date</th>
                            <th className="text-center">Score</th>
                            <th className="text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getFilteredResults().map(r => (
                            <tr key={r.id} className="group/item">
                              <td className="font-black text-slate-700 text-sm uppercase tracking-tight">{r.test.subject.name}</td>
                              <td><span className="utopia-badge bg-slate-50 text-slate-500 font-black text-[9px] uppercase tracking-widest px-2">{r.test.title}</span></td>
                              <td className="text-center font-mono text-[10px] text-slate-400 font-bold">{new Date(r.test.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                              <td className="text-center">
                                {editingResult?.id === r.id ? (
                                  <input type="number" className="utopia-input w-20 text-center h-10 font-black" value={editScore} onChange={e => setEditScore(e.target.value)} onBlur={() => handleUpdateScore(r.id, parseFloat(editScore))} />
                                ) : (
                                  <span className={`text-sm font-black px-4 py-2 rounded-2xl ${r.score < 50 ? 'bg-rose-50 text-rose-500' : 'bg-teal-50 text-teal-600'}`}>{r.score}%</span>
                                )}
                              </td>
                              <td className="text-right">
                                <div className="flex justify-end gap-2 opacity-0 group-hover/item:opacity-100 transition-opacity">
                                  <button className="p-2 rounded-xl bg-slate-50 text-blue-400 hover:bg-blue-50 transition-colors" onClick={() => { setEditingResult(r); setEditScore(r.score.toString()); }}><Edit size={14} /></button>
                                  <button className="p-2 rounded-xl bg-slate-50 text-rose-300 hover:bg-rose-50 transition-colors" onClick={() => handleDeleteResult(r.id)}><Trash2 size={14} /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-40 text-center text-slate-200">
                      <History size={100} className="mx-auto mb-8 opacity-5" />
                      <p className="font-black uppercase tracking-widest text-slate-300">No results found for the selected filters.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RECORD RESULTS MODAL */}
      {activeModal === "recordResults" && selectedStudent && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal-content max-w-4xl p-0 overflow-hidden flex flex-col h-[85vh]">
            <div className="utopia-modal-header bg-slate-50 p-5 border-b border-slate-200 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-teal-600 border border-slate-200"><Database size={20} /></div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">Record Assessment Results</h3>
                  <p className="text-xs text-slate-500">Enter scores for {selectedStudent.name}</p>
                </div>
              </div>
              <button onClick={closeModal} className="p-2 hover:bg-white rounded-lg shadow-sm text-slate-400 transition-all"><X size={18} /></button>
            </div>

            <form onSubmit={handleRecordSubmit} className="flex-1 flex flex-col overflow-hidden">
              <div className="p-6 grid grid-cols-3 gap-4 bg-white border-b border-slate-200 shrink-0">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-600">Assessment Type</label>
                  <select className="utopia-input h-14 text-sm" value={recordFormData.examType} onChange={e => setRecordFormData({ ...recordFormData, examType: e.target.value })} required>
                    <option value="">Select Assessment Type...</option>
                    {ASSESSMENT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-600">Term</label>
                  <select
                    className="utopia-input h-14 text-sm"
                    value={recordFormData.term}
                    onChange={e => setRecordFormData({ ...recordFormData, term: e.target.value })}
                    required
                  >
                    <option value="">Select Term...</option>
                    {TERM_OPTIONS.map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-600">Date</label>
                  <div className="relative">
                    <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="date" className="utopia-input h-14 pl-10 text-sm" value={recordFormData.testDate} onChange={e => setRecordFormData({ ...recordFormData, testDate: e.target.value })} required />
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-white min-h-[300px]">
                {loadingSubjects ? (
                  <div className="py-16 text-center">
                    <Loader2 className="animate-spin mx-auto text-slate-400" size={32} />
                    <p className="text-xs text-slate-400 mt-3">Loading subjects...</p>
                  </div>
                ) : classSubjects.length > 0 ? (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Subject</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide w-32">Score (0-100)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {classSubjects.map(s => (
                          <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-slate-800">{getSubjectLabel(s)}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <input
                                type="number"
                                className="utopia-input w-24 h-10 text-center text-sm font-semibold bg-white border border-slate-300 hover:border-teal-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-200 transition-all no-spinner"
                                placeholder="0"
                                value={recordFormData.scores[s.id] || ""}
                                onChange={e => handleScoreChange(s.id, e.target.value)}
                                max="100"
                                min="0"
                                step="0.1"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-16 text-center">
                    <BookOpen size={36} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-xs font-medium text-slate-400">No subjects found for this class</p>
                    <p className="text-[10px] text-slate-300 mt-1">Please ensure the class has subjects assigned</p>
                  </div>
                )}
              </div>

              <div className="p-5 border-t border-slate-200 bg-white shrink-0">
                {recordError && <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-600 text-xs font-medium flex items-center gap-2 mb-4"><AlertCircle size={14} /> {recordError}</div>}
                {recordMessage && <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-teal-600 text-xs font-medium flex items-center gap-2 mb-4"><CheckCircle size={14} /> {recordMessage}</div>}
                <button type="submit" className="utopia-btn-primary w-full py-3 text-sm font-semibold shadow-lg shadow-teal-100 flex items-center justify-center gap-2" disabled={recording}>
                  {recording ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Results
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentsPage;
